import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { PersonalReportSummaryJson } from '../../data/mockPersonalReportSummary';

export function AbilityAnalysis({
  effectiveRadar,
}: {
  effectiveRadar: PersonalReportSummaryJson['radar'];
}) {
  const sorted = useMemo(
    () => [...effectiveRadar].sort((a, b) => b.avg - a.avg),
    [effectiveRadar],
  );
  const top3 = sorted.slice(0, 3);
  const showTop2Improve = effectiveRadar.length >= 5;
  const top2Improve = useMemo(() => {
    if (!showTop2Improve) return [];
    const top3Keys = new Set(top3.map((r) => r.code || r.label || ''));
    return [...sorted]
      .reverse()
      .filter((r) => !top3Keys.has(r.code || r.label || ''))
      .slice(0, 2);
  }, [sorted, showTop2Improve, top3]);
  const maxScore = 7;

  const radarOption = useMemo(() => {
    if (!effectiveRadar.length) return null;
    const indicators = effectiveRadar.map((r) => ({
      name: (r.label || r.code || '?').slice(0, 6),
      max: 7,
    }));
    const values = effectiveRadar.map((r) => r.avg);
    return {
      tooltip: {},
      color: ['#176B5B'],
      radar: {
        indicator: indicators,
        radius: '62%',
        splitNumber: 5,
        axisName: { fontSize: 10, color: '#71807a' },
        splitLine: { lineStyle: { color: '#dde3e0' } },
        splitArea: { areaStyle: { color: ['#fffefa', '#f1f3f2'] } },
        axisLine: { lineStyle: { color: '#afcbc2' } },
      },
      series: [
        {
          type: 'radar' as const,
          areaStyle: { opacity: 0.18, color: '#176B5B' },
          lineStyle: { width: 1.5, color: '#176B5B' },
          itemStyle: { color: '#176B5B' },
          data: [{ value: values, name: '360 综合' }],
        },
      ],
    };
  }, [effectiveRadar]);

  return (
    <section className="report-ability-analysis" aria-label="素质项优势与待提升">
      <div className="report-ability-ranks">
        <RankBlock title="优势 TOP3" items={top3} maxScore={maxScore} tone="adv" />
        {showTop2Improve && top2Improve.length > 0 ? (
          <RankBlock
            title="待提升 TOP2"
            items={top2Improve}
            maxScore={maxScore}
            tone="improve"
            compact
          />
        ) : null}
      </div>
      <div className="report-ability-radar">
        {radarOption ? (
          <ReactECharts option={radarOption} style={{ height: 240 }} notMerge lazyUpdate />
        ) : (
          <div className="report-ability-empty">暂无素质维度数据</div>
        )}
      </div>
    </section>
  );
}

function RankBlock({
  title,
  items,
  maxScore,
  tone,
  compact = false,
}: {
  title: string;
  items: PersonalReportSummaryJson['radar'];
  maxScore: number;
  tone: 'adv' | 'concern' | 'improve';
  compact?: boolean;
}) {
  return (
    <div className={`report-rank-block${compact ? ' report-rank-block--compact' : ''}`}>
      <div className={`report-rank-title report-rank-title--${tone}`}>{title}</div>
      {items.length ? (
        <ul className="report-rank-list">
          {items.map((item, i) => (
            <li key={item.code || item.label || i} className="report-rank-item">
              <div className="report-rank-meta">
                <span className="report-rank-index">{i + 1}</span>
                <span className="report-rank-label">{item.label || item.code}</span>
                <span className="report-rank-score">{item.avg.toFixed(2)}</span>
              </div>
              <div className="report-rank-bar-track">
                <div
                  className={`report-rank-bar-fill report-rank-bar-fill--${tone}`}
                  style={{ width: `${(item.avg / maxScore) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="report-rank-empty">暂无数据</div>
      )}
    </div>
  );
}
